import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Form, InputNumber, Tooltip, message, Switch } from 'antd';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import HeadlineTipImage1 from 'assets/uniprofile/headlinetip1.jpg';
import HeadlineTipImage2 from 'assets/uniprofile/headlinetip2.jpg';
import HeadlinesEditor from 'components/AIHeadlinesEditor';
import AiTextEditor from 'components/AI/AiTextEditor';
import PhotoEditorModal from 'components/PhotoEditorModal';
import { EAIEditorType } from 'types/ai';
import ProfileTip from 'assets/practiceFront/PF_profile_tip.png';
import { QuestionCircleOutlined } from '@ant-design/icons';
import s from './s.module.less';
import { ProfessionalProfileInfo } from 'types/newUniprofile';
import DefaultProfileAvatar from 'assets/common/defaultProviderAvatar.svg';
import { editProfessionalProfile, getAIAiAboutMe } from 'api/newUniprofile';
import { isEqual } from 'lodash';
SwiperCore.use([Navigation]);

interface Props {
    initValue?: ProfessionalProfileInfo;
    isEditing?: boolean;
    refetch: () => void;
    showTitle?: boolean;
    scheduleToken?: string;
}

const ProfessionalProfile = forwardRef((props: Props, ref) => {
    const { isEditing, initValue, refetch, showTitle = false, scheduleToken } = props;
    const [form] = Form.useForm<ProfessionalProfileInfo>();
    const prevInitValueRef = useRef<ProfessionalProfileInfo>();
    const [isAboutMeAiDraft, setIsAboutMeAiDraft] = useState(false);
    const [isAboutMeAiDraftLoading, setIsAboutMeAiDraftLoading] = useState(false);

    useEffect(() => {
        if (initValue && !isEqual(prevInitValueRef.current, initValue)) {
            prevInitValueRef.current = initValue;
            form.setFieldsValue(initValue);
        }
    }, [initValue]);

    const onSubmit = async () => {
        try {
            const params = await form.validateFields();
            const result = await editProfessionalProfile(
                {
                    ...initValue,
                    ...params,
                },
                scheduleToken,
            );
            if (result.error) {
                message.error(result.error);
            } else {
                refetch();
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    useEffect(() => {
        const fetchAiAboutMe = async () => {
            try {
                setIsAboutMeAiDraftLoading(true);
                const res = await getAIAiAboutMe(scheduleToken);
                if (!res.error) {
                    form.setFieldsValue({
                        intro: res.data.data,
                    });
                }
            } catch (error) {
                console.error(error);
            } finally {
                setIsAboutMeAiDraftLoading(false);
            }
        };
        if (isAboutMeAiDraft) {
            fetchAiAboutMe();
        }
    }, [isAboutMeAiDraft]);

    useImperativeHandle(ref, () => ({
        onSubmit,
        onValidate: () => {
            return form.validateFields();
        },
    }));

    const renderEditing = () => {
        return (
            <div className={s.wrap}>
                <Form form={form} initialValues={{ remember: true }} autoComplete="off" layout="vertical">
                    <Form.Item noStyle>
                        <Form.Item
                            label="Years of experience in healthcare"
                            name="yearExp"
                            rules={[{ required: true, message: 'Years of experience is required' }]}
                        >
                            <InputNumber min={0} controls={false} style={{ width: '320px' }} />
                        </Form.Item>
                        <Form.Item
                            style={{ width: '736px' }}
                            label={
                                <div className={s.formLabel}>
                                    Headlines
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        overlayInnerStyle={{ padding: '24px' }}
                                        color="#FFFFFF"
                                        title={
                                            <>
                                                <div className={s.tipText}>
                                                    Provide a punchy headline about yourself to stand out. Your headline
                                                    will appear on all channels' profiles, including your Practice Front
                                                    and Klarity profile page.
                                                </div>
                                                <Swiper navigation>
                                                    <SwiperSlide key={0} className={s.leftWrap}>
                                                        <div style={{ width: '344px', margin: '0 auto' }}>
                                                            <img src={HeadlineTipImage1} />
                                                        </div>
                                                    </SwiperSlide>
                                                    <SwiperSlide key={1} className={s.leftWrap}>
                                                        <div style={{ width: '344px', margin: '0 auto' }}>
                                                            <img src={HeadlineTipImage2} />
                                                        </div>
                                                    </SwiperSlide>
                                                </Swiper>
                                            </>
                                        }
                                    >
                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                    </Tooltip>
                                </div>
                            }
                            name="headLine"
                            rules={[
                                { required: true, message: 'Headline is a required field' },
                                { max: 220, message: 'Maximum 220 characters allowed' },
                            ]}
                        >
                            <HeadlinesEditor
                                options={initValue?.headLineList}
                                conversationId={initValue?.headLineConversationId}
                            />
                        </Form.Item>
                        <Form.Item
                            style={{ width: '736px' }}
                            label={
                                <div className={s.formLabel}>
                                    About me
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        color="#FFFFFF"
                                        title={
                                            <div className={s.tipText2}>
                                                What would you like to share with your patients about yourself?
                                            </div>
                                        }
                                    >
                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                    </Tooltip>
                                </div>
                            }
                            name="intro"
                            rules={[{ required: true, message: 'Headline is a required field' }]}
                        >
                            <div className="position-relative">
                                <div className={s.aiDraftBadge}>
                                    <span className={s.aiIcon}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M7.47969 11.4796L9.02563 6.8418H9.97431L11.5203 11.4796L16.1581 13.0256V13.9743L11.5203 15.5202L9.97431 20.158H9.02563L7.47969 15.5202L2.84186 13.9743V13.0256L7.47969 11.4796Z"
                                                fill="url(#paint0_linear_507_21172)"
                                            />
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M16.2297 5.22963L17.0256 2.8418H17.9743L18.7703 5.22963L21.1581 6.02557V6.97425L18.7703 7.7702L17.9743 10.158H17.0256L16.2297 7.7702L13.8419 6.97425V6.02557L16.2297 5.22963Z"
                                                fill="url(#paint1_linear_507_21172)"
                                            />
                                            <defs>
                                                <linearGradient
                                                    id="paint0_linear_507_21172"
                                                    x1="12"
                                                    y1="2.8418"
                                                    x2="12"
                                                    y2="20.158"
                                                    gradientUnits="userSpaceOnUse"
                                                >
                                                    <stop stop-color="#0E62F4" />
                                                    <stop offset="1" stop-color="#BB5BFF" />
                                                </linearGradient>
                                                <linearGradient
                                                    id="paint1_linear_507_21172"
                                                    x1="12"
                                                    y1="2.8418"
                                                    x2="12"
                                                    y2="20.158"
                                                    gradientUnits="userSpaceOnUse"
                                                >
                                                    <stop stop-color="#0E62F4" />
                                                    <stop offset="1" stop-color="#BB5BFF" />
                                                </linearGradient>
                                            </defs>
                                        </svg>
                                    </span>
                                    <span>AI-Assisted Draft</span>
                                    <Switch checked={isAboutMeAiDraft} onChange={setIsAboutMeAiDraft} />
                                </div>
                                <AiTextEditor
                                    maxLength={1000}
                                    type={EAIEditorType.ABOUTME}
                                    placeholder="Start writing, then use Ava to polish, adjust tone, or refine your style."
                                    style={{ height: '85px' }}
                                    conversionId={initValue?.aboutMeConversationId}
                                    generatingValue={isAboutMeAiDraftLoading}
                                    value={form.getFieldValue('intro')}
                                />
                            </div>
                        </Form.Item>
                        <Form.Item
                            style={{ width: '350px' }}
                            label={
                                <div className={s.formLabel}>
                                    Profile photo
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        overlayInnerStyle={{ padding: '12px' }}
                                        color="#FFFFFF"
                                        title={
                                            <div className={s.tipTextWrap}>
                                                <div>
                                                    Upload a professional photo of yourself with a preferred background.
                                                    A top-notch profile photo is likely to generate 3x more clicks. You
                                                    can crop and rotate the image as needed.
                                                </div>
                                                <div>
                                                    Photos that include a background typically achieve the best possible
                                                    appearance and visibility across various channels.{' '}
                                                    <img src={ProfileTip} />
                                                </div>
                                            </div>
                                        }
                                    >
                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                    </Tooltip>
                                </div>
                            }
                            name="photo"
                            rules={[{ required: true, message: 'Profile photo is a required field' }]}
                        >
                            <PhotoEditorModal borderRadius title="Edit photo" providerId={initValue?.providerId!} />
                        </Form.Item>
                    </Form.Item>
                </Form>
            </div>
        );
    };

    const renderReview = () => {
        return (
            <div className={s.subModule}>
                <div className={s.row}>
                    <div className={s.displayItem}>
                        <div className={`${s.label} ${s.required}`}>Years of experience in healthcare</div>
                        <div className={s.value}>{initValue?.yearExp || '-'}</div>
                    </div>
                </div>
                <div className={s.row}>
                    <div className={s.displayItem}>
                        <div className={`${s.label} ${s.required}`}>Headline</div>
                        <div className={s.value}>{initValue?.headLine || '-'}</div>
                    </div>
                </div>
                <div className={s.row}>
                    <div className={s.displayItem}>
                        <div className={`${s.label} ${s.required}`}>About me</div>
                        <div
                            className={s.value}
                            dangerouslySetInnerHTML={{
                                __html: initValue?.intro?.replace(/\n/g, '<br />') || '-',
                            }}
                        />
                    </div>
                </div>
                <div className={s.row}>
                    <div className={s.displayItem}>
                        <div className={`${s.label} ${s.required}`}>Profile photo</div>
                        <img className={s.profileAvatar} src={initValue?.photo || DefaultProfileAvatar} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            {showTitle && <div className={s.title}>Professional profile</div>}
            {isEditing ? renderEditing() : renderReview()}
        </div>
    );
});

export default ProfessionalProfile;
